import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Select,
  DatePicker,
  Input,
  Badge,
  Tooltip,
  Statistic,
  Modal,
} from 'antd';
import {
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  DollarOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const CustomerDetailsTable = ({ visible, onClose, filterType, data, loading, onNavigate }) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Mock customer data - replace with actual API call
  const _mockCustomers = [
    {
      id: 1,
      companyName: 'TechCorp Ltd',
      contactPerson: 'John Smith',
      email: 'john@techcorp.com',
      phone: '+1-555-0101',
      status: 'Active',
      plan: 'Enterprise',
      joinDate: '2024-01-15',
      lastActive: '2024-03-15',
      messagesThisMonth: 12456,
      totalMessages: 156789,
      revenue: 2456.78,
      totalRevenue: 25678.90,
      partnerReferred: false,
      partnerName: null,
      billingStatus: 'Paid',
      nextBillingDate: '2024-04-15',
      apiUsage: 15678,
      errorRate: 0.5,
      averageResponseTime: 245
    },
    {
      id: 2,
      companyName: 'StartupXYZ',
      contactPerson: 'Sarah Johnson',
      email: 'sarah@startupxyz.com',
      phone: '+1-555-0102',
      status: 'Active',
      plan: 'Business',
      joinDate: '2024-02-20',
      lastActive: '2024-03-14',
      messagesThisMonth: 8734,
      totalMessages: 67890,
      revenue: 1234.56,
      totalRevenue: 12456.78,
      partnerReferred: true,
      partnerName: 'Digital Solutions Inc',
      billingStatus: 'Paid',
      nextBillingDate: '2024-04-20',
      apiUsage: 9876,
      errorRate: 0.3,
      averageResponseTime: 198
    },
    {
      id: 3,
      companyName: 'RetailPlus',
      contactPerson: 'Mike Wilson',
      email: 'mike@retailplus.com',
      phone: '+1-555-0103',
      status: 'Inactive',
      plan: 'Basic',
      joinDate: '2023-12-10',
      lastActive: '2024-02-28',
      messagesThisMonth: 0,
      totalMessages: 23456,
      revenue: 0,
      totalRevenue: 3456.78,
      partnerReferred: false,
      partnerName: null,
      billingStatus: 'Overdue',
      nextBillingDate: '2024-03-10',
      apiUsage: 0,
      errorRate: 0,
      averageResponseTime: 0
    },
    {
      id: 4,
      companyName: 'HealthcarePro',
      contactPerson: 'Dr. Emily Chen',
      email: 'emily@healthcarepro.com',
      phone: '+1-555-0104',
      status: 'Active',
      plan: 'Professional',
      joinDate: '2024-01-05',
      lastActive: '2024-03-15',
      messagesThisMonth: 15678,
      totalMessages: 189012,
      revenue: 3456.78,
      totalRevenue: 34567.89,
      partnerReferred: true,
      partnerName: 'Healthcare Solutions Ltd',
      billingStatus: 'Paid',
      nextBillingDate: '2024-04-05',
      apiUsage: 18901,
      errorRate: 0.2,
      averageResponseTime: 167
    },
    {
      id: 5,
      companyName: 'EduTech Solutions',
      contactPerson: 'David Park',
      email: 'david@edutech.com',
      phone: '+1-555-0105',
      status: 'Trial',
      plan: 'Trial',
      joinDate: '2024-03-10',
      lastActive: '2024-03-15',
      messagesThisMonth: 1234,
      totalMessages: 1234,
      revenue: 0,
      totalRevenue: 0,
      partnerReferred: false,
      partnerName: null,
      billingStatus: 'Trial',
      nextBillingDate: '2024-04-10',
      apiUsage: 1234,
      errorRate: 1.2,
      averageResponseTime: 298
    }
  ];

  useEffect(() => {
    if (data && data.companies) {
      setCustomers(data.companies);
      setPagination(prev => ({ 
        ...prev, 
        total: data.totalrecords || data.companies.length,
        current: data.pagination?.currentPage || 1
      }));
    }
  }, [data]);
 useEffect(() => {
    filterCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchText, statusFilter, planFilter, dateRange]);

  const filterCustomers = () => {
    let filtered = [...customers];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(customer =>
        (customer.companyname || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (customer.emailid || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (customer.contactno || '').toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(customer => 
        customer.status === 1 ? 'active' : 'inactive' === statusFilter
      );
    }

    // Plan filter - based on company_type
    if (planFilter !== 'all') {
      filtered = filtered.filter(customer => 
        (customer.company_type || '').toLowerCase() === planFilter
      );
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter(customer => {
        const joinDate = moment(customer.entrytime);
        return joinDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    setFilteredCustomers(filtered);
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  const _getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'trial': return 'blue';
      case 'suspended': return 'orange';
      default: return 'default';
    }
  };

  const _getPlanColor = (plan) => {
    switch (plan.toLowerCase()) {
      case 'enterprise': return 'purple';
      case 'professional': return 'blue';
      case 'business': return 'green';
      case 'basic': return 'orange';
      case 'trial': return 'gray';
      default: return 'default';
    }
  };

  const _getBillingStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'pending': return 'orange';
      case 'trial': return 'blue';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Sr No',
      key: 'srno',
      width: 60,
      render: (_, record, index) => index + 1,
    },
    {
      title: 'Company Details',
      key: 'company',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.companyname}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <MailOutlined /> {record.emailid}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <PhoneOutlined /> {record.countrycode} {record.contactno}
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            ID: {record.companyid}
          </div>
        </div>
      ),
    },
    {
      title: 'Status & Performance',
      key: 'statusPerformance',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Tag color={record.status === 1 ? 'green' : 'red'} style={{ marginBottom: 4 }}>
              {record.status === 1 ? 'Active' : 'Inactive'}
            </Tag>
            <Tag color={record.activecustomer > 0 ? 'green' : 'orange'}>
              {record.activecustomer > 0 ? 'Performing' : 'Pending'}
            </Tag>
          </div>
          <div style={{ fontSize: '11px', marginBottom: 4 }}>
            <span style={{ color: '#666' }}>Quality Rating:</span>
            <Tag 
              color={
                record.activecustomer > 15 ? 'green' : 
                record.activecustomer > 5 ? 'orange' : 'red'
              } 
              size="small"
              style={{ marginLeft: 4 }}
            >
              {record.activecustomer > 15 ? 'High' : 
               record.activecustomer > 5 ? 'Medium' : 'Low'}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Solution Partners',
      key: 'solutionPartners',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              {record.totalcustomer || 0}
            </Text>
            <Text style={{ fontSize: '11px', color: '#666', marginLeft: 4 }}>
              Total Referred
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: 2 }}>
            <span style={{ fontSize: '11px' }}>
              <span style={{ color: '#52c41a' }}>Active: {record.activecustomer || 0}</span>
            </span>
          </div>
          <div style={{ fontSize: '11px' }}>
            <span style={{ color: '#fa8c16' }}>Pending: {record.pendingcustomer || 0}</span>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.totalcustomer || 0) - (b.totalcustomer || 0),
    },
    {
      title: 'Revenue & Commission',
      key: 'revenueCommission',
      width: 160,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
              ${((record.activecustomer || 0) * 50).toFixed(2)}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>Total Revenue</div>
          </div>
          <div style={{ marginBottom: 4 }}>
            <Text style={{ fontSize: '11px', color: '#1890ff' }}>
              ${((record.activecustomer || 0) * 15).toFixed(2)}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>Monthly Revenue</div>
          </div>
          <div>
            <Text style={{ fontSize: '11px', color: '#722ed1' }}>
              ${((record.activecustomer || 0) * 5).toFixed(2)}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>Commission</div>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.activecustomer || 0) - (b.activecustomer || 0),
    },
    {
      title: 'Messages & Activity',
      key: 'messagesActivity',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: '12px' }}>
              {((record.activecustomer || 0) * 1500).toLocaleString()}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>Total Messages</div>
          </div>
          <div style={{ marginBottom: 4 }}>
            <Text style={{ fontSize: '11px', color: '#1890ff' }}>
              {((record.activecustomer || 0) * 150).toLocaleString()}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>This Month</div>
          </div>
          <div>
            <Text style={{ fontSize: '11px', color: '#fa8c16' }}>
              {Math.floor(Math.random() * 5)}
            </Text>
            <div style={{ fontSize: '10px', color: '#666' }}>Support Tickets</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Billing',
      key: 'billing',
      width: 130,
      render: (_, record) => (
        <div>
          <Tag color={record.status === 1 ? 'green' : 'red'}>
            {record.status === 1 ? 'Active' : 'Inactive'}
          </Tag>
          <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
            Next: {moment(record.updatetime).add(30, 'days').format('MMM DD')}
          </div>
          <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
            ${((record.activecustomer || 0) * 25).toFixed(2)}/month
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Customer">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Send Message">
            <Button
              type="text"
              size="small"
              icon={<MessageOutlined />}
              onClick={() => handleSendMessage(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleViewCustomer = (customer) => {
    // Navigate to customer detail page or show detailed modal
    onNavigate?.(`customer/${customer.id}`);
  };

  const handleEditCustomer = (customer) => {
    // Open edit modal or navigate to edit page
    console.log('Edit customer:', customer);
  };

  const handleSendMessage = (customer) => {
    // Open message sending modal
    console.log('Send message to:', customer);
  };

  const exportData = () => {
    // Export filtered customer data
    const dataStr = JSON.stringify(filteredCustomers, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customers_${moment().format('YYYY-MM-DD')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Customer Details - {filterType || 'All Customers'}</span>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} size="small">
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportData} size="small">
              Export
            </Button>
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      style={{ top: 20 }}
    >
      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Search
              placeholder="Search customers..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="trial">Trial</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Plan"
              value={planFilter}
              onChange={setPlanFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Plans</Option>
              <Option value="enterprise">Enterprise</Option>
              <Option value="professional">Professional</Option>
              <Option value="business">Business</Option>
              <Option value="basic">Basic</Option>
              <Option value="trial">Trial</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Space wrap>
              <Badge count={filteredCustomers.length} showZero>
                <Button icon={<FilterOutlined />}>
                  Filtered Results
                </Button>
              </Badge>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {filterType === 'directCustomers' && (
          <>
            <Col xs={6}>
              <Statistic
                title="Total Direct Customers"
                value={data?.totalrecords || filteredCustomers.length}
                prefix={<UserOutlined />}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Active Direct Customers"
                value={filteredCustomers.filter(c => c.status === 1).length}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Direct Customer Revenue"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 30), 0)}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Direct Customer Messages"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 800), 0)}
                prefix={<MessageOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </>
        )}
        
        {filterType === 'partnerCustomers' && (
          <>
            <Col xs={6}>
              <Statistic
                title="Total Partner Customers"
                value={data?.totalrecords || filteredCustomers.length}
                prefix={<UserOutlined />}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Active Partner Customers"
                value={filteredCustomers.filter(c => c.status === 1).length}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Partner Customer Revenue"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 40), 0)}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Partner Referral Bonus"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 8), 0)}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </>
        )}
        
        {(filterType === 'customers' || !filterType) && (
          <>
            <Col xs={6}>
              <Statistic
                title="Total Customers"
                value={data?.totalrecords || filteredCustomers.length}
                prefix={<UserOutlined />}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Total Customers Referred"
                value={data?.totalreferredcustomers || filteredCustomers.reduce((sum, c) => sum + (c.totalcustomer || 0), 0)}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Total Platform Revenue"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 35), 0)}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={6}>
              <Statistic
                title="Total Messages Sent"
                value={filteredCustomers.reduce((sum, c) => sum + ((c.activecustomer || 0) * 1200), 0)}
                prefix={<MessageOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </>
        )}
      </Row>

      {/* Customer Table */}
      <Table
        columns={columns}
        dataSource={filteredCustomers}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} customers`,
        }}
        onChange={handleTableChange}
        rowKey="id"
        size="small"
        scroll={{ x: 1200 }}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};

export default CustomerDetailsTable;
